import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface PublicWorldCupConfig {
  organization_slug: string;
  competition_id: string;
}

// Module-level cache + in-flight promise so all callers share one network round-trip.
let cached: PublicWorldCupConfig | null | undefined; // undefined = not loaded, null = none configured
let inflight: Promise<PublicWorldCupConfig | null> | null = null;

async function fetchConfig(): Promise<PublicWorldCupConfig | null> {
  if (cached !== undefined) return cached;
  if (inflight) return inflight;
  inflight = (async () => {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', 'public_worldcup')
      .maybeSingle();
    const value = (data?.value ?? null) as PublicWorldCupConfig | null;
    cached = error || !value?.organization_slug || !value?.competition_id ? null : value;
    inflight = null;
    return cached;
  })();
  return inflight;
}

/**
 * Returns the platform-wide public World Cup config (the competition that
 * is reachable via the friendly /worldcup URL). Cached at module scope.
 */
export function usePublicWorldCupConfig(): PublicWorldCupConfig | null {
  const [config, setConfig] = useState<PublicWorldCupConfig | null>(cached ?? null);

  useEffect(() => {
    if (cached !== undefined) {
      setConfig(cached);
      return;
    }
    let cancelled = false;
    fetchConfig().then((c) => {
      if (!cancelled) setConfig(c);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return config;
}

/**
 * Returns true if the given competition is the configured public World Cup
 * (and therefore should be shared via the short /worldcup link).
 */
export function useIsPublicWorldCup(competitionId: string | undefined | null): boolean {
  const config = usePublicWorldCupConfig();
  return !!config && !!competitionId && config.competition_id === competitionId;
}
